import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import {
  _get_my_business_orders,
  _get_restaurant_order_metrics,
  _get_business_support_manager,
} from "../common/axios_services";
import { showErrorNotification } from "../common/utils/notification";

export const useRestaurantData = (userProfile, navigate) => {
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [orderHist, setOrderHist] = useState({
    orders: [],
    pagination: {},
    summary: {},
  });
  const [orderMetrics, setOrderMetrics] = useState([]);
  const [supportManager, setSupportManager] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getSalesOrders = async (params = {}) => {
    if (!userProfile?.restaurantId)
      return { orders: [], pagination: {}, summary: {} };
    try {
      setSpinnerLoading(true);
      const requestParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        dashboardSection: 'todays_sales',
        ...params,
      };
      const response = await _get_my_business_orders(requestParams);
      const data = response.data.data?.data || response.data.data;
      return {
        orders: data.orders || [],
        pagination: data.pagination || {},
        summary: data.summary || {},
      };
    } catch (err) {
      handleApiError(err, navigate);
      return { orders: [], pagination: {}, summary: {} };
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getPendingOrders = async (params = {}) => {
    if (!userProfile?.restaurantId)
      return { orders: [], pagination: {}, summary: {} };
    try {
      setSpinnerLoading(true);
      const requestParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        dashboardSection: 'pending_delivery',
        ...params,
      };
      const response = await _get_my_business_orders(requestParams);
      const data = response.data.data?.data || response.data.data;
      return {
        orders: data.orders || [],
        pagination: data.pagination || {},
        summary: data.summary || {},
      };
    } catch (err) {
      handleApiError(err, navigate);
      return { orders: [], pagination: {}, summary: {} };
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getTodaysDeliveryOrders = async (params = {}) => {
    if (!userProfile?.restaurantId)
      return { orders: [], pagination: {}, summary: {} };
    try {
      setSpinnerLoading(true);
      const requestParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        dashboardSection: 'todays_delivery',
        ...params,
      };
      const response = await _get_my_business_orders(requestParams);
      const data = response.data.data?.data || response.data.data;
      return {
        orders: data.orders || [],
        pagination: data.pagination || {},
        summary: data.summary || {},
      };
    } catch (err) {
      handleApiError(err, navigate);
      return { orders: [], pagination: {}, summary: {} };
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getDeliveredOrders = async (params = {}) => {
    if (!userProfile?.restaurantId)
      return { orders: [], pagination: {}, summary: {} };
    try {
      setSpinnerLoading(true);
      const requestParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        dashboardSection: 'completed',
        ...params,
      };
      const response = await _get_my_business_orders(requestParams);
      const data = response.data.data?.data || response.data.data;
      return {
        orders: data.orders || [],
        pagination: data.pagination || {},
        summary: data.summary || {},
      };
    } catch (err) {
      handleApiError(err, navigate);
      return { orders: [], pagination: {}, summary: {} };
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getRestaurantOrderHist = async (params = {}) => {
    if (!userProfile?.restaurantId) return;
    try {
      setSpinnerLoading(true);
      const requestParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        ...params,
      };
      const get_hist = await _get_my_business_orders(requestParams);
      setOrderHist({
        orders: get_hist.data.data.orders || [],
        pagination: get_hist.data.data.pagination || {},
        summary: get_hist.data.data.summary || {},
      });
    } catch (err) {
      handleApiError(err, navigate);
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getRestaurantOrderMetrics = async () => {
    try {
      setSpinnerLoading(true);
      const _get_metrics = await _get_restaurant_order_metrics();
      setOrderMetrics(_get_metrics.data.data);
    } catch (err) {
      handleApiError(err, navigate);
    } finally {
      setSpinnerLoading(false);
    }
  };

  const getSupportManager = async () => {
    try {
      const response = await _get_business_support_manager();
      setSupportManager(response.data?.data);
    } catch (err) {
      setSupportManager(null);
    }
  };

  const filterTodayOrders = (orders) => {
    const today = moment().startOf("day");
    return orders.filter((order) =>
      moment(order.createdAt || order.created_at).isSame(today, "day")
    );
  };

  const filterPreviousOrders = (orders) => {
    const today = moment().startOf("day");
    return orders.filter((order) =>
      moment(order.createdAt || order.created_at).isBefore(today, "day")
    );
  };

  const todaysOrders = useMemo(
    () => (orderHist?.orders ? filterTodayOrders(orderHist.orders) : []),
    [orderHist?.orders]
  );

  const processedPreviousOrders = useMemo(() => {
    const previousOrders = orderHist?.orders
      ? filterPreviousOrders(orderHist.orders)
      : [];
    return previousOrders.map((order) => ({
      ...order,
      status: "delivered",
      action: null,
    }));
  }, [orderHist?.orders]);

  useEffect(() => {
    if (userProfile?.restaurantId) {
      getRestaurantOrderHist();
    }
  }, [userProfile?.restaurantId]);

  useEffect(() => {
    getRestaurantOrderMetrics();
    getSupportManager();
  }, []);

  return {
    spinnerLoading,
    setSpinnerLoading,
    orderHist,
    orderMetrics,
    todaysOrders,
    processedPreviousOrders,
    supportManager,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    getSalesOrders,
    getTodaysDeliveryOrders,
    getPendingOrders,
    getDeliveredOrders,
    getRestaurantOrderHist,
    getRestaurantOrderMetrics,
    getSupportManager,
  };
};

const handleApiError = (err, navigate) => {
  showErrorNotification(err?.response?.data?.message||err?.message||"An error occurred");
};
